import React from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  Phone,
  MapPin,
  Star,
  Crown,
  Edit2,
  Trash2,
  Gift,
  Tag,
  Settings,
  UserCheck,
  UserX,
  ToggleRight,
  ToggleLeft,
  FileText
} from 'lucide-react';
import { getRoleStyle, getStatusStyle, getSkillLabel } from '../utils/userHelpers';

/**
 * UserCard Component
 * Displays a user card with all user information, role-specific details, and action buttons
 *
 * @param {Object} userData - User data object
 * @param {Function} onEdit - Callback for edit button
 * @param {Function} onDelete - Callback for delete button
 * @param {Function} onStatusChange - Callback for status change
 * @param {Function} onOpenPoints - Callback for manage points
 * @param {Function} onOpenVouchers - Callback for manage vouchers
 * @param {Function} onUpdateAgent - Callback for updating agent status/commission
 * @param {Function} onViewCertificates - Callback for viewing vendor certificates
 * @param {boolean} isSuper - Whether the current user is a super admin
 */
const UserCard = ({
  userData,
  onEdit,
  onDelete,
  onStatusChange,
  onOpenPoints,
  onOpenVouchers,
  onUpdateAgent,
  onViewCertificates,
  isSuper
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 p-6"
    >
      {/* Header Section */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
              {userData.firstName?.[0] || userData.email[0].toUpperCase()}
            </div>
            {userData.status === 'ACTIVE' && (
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-lg">
              {userData.fullName || `${userData.firstName} ${userData.lastName}`}
            </h3>
            <div className="flex items-center space-x-2 mt-1">
              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRoleStyle(userData.role)}`}>
                {userData.role}
              </span>
              {userData.isSuper && (
                <Crown className="w-4 h-4 text-yellow-500" title="Super Admin" />
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusStyle(userData.status)}`}>
            {userData.status}
          </span>
        </div>
      </div>

      {/* Contact Information */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center text-gray-600 text-sm">
          <Mail className="w-4 h-4 mr-2" />
          {userData.email}
        </div>
        {userData.phone && (
          <div className="flex items-center text-gray-600 text-sm">
            <Phone className="w-4 h-4 mr-2" />
            {userData.phone}
          </div>
        )}
        {userData.city && (
          <div className="flex items-center text-gray-600 text-sm">
            <MapPin className="w-4 h-4 mr-2" />
            {userData.city}, {userData.country}
          </div>
        )}
        {(userData.role === 'customer' || userData.role === 'referral') && typeof userData.pointsBalance !== 'undefined' && (
          <div className="flex items-center text-gray-600 text-sm">
            <Star className="w-4 h-4 mr-2" />
            {userData.pointsBalance || 0} points available
          </div>
        )}
      </div>

      {/* Vendor-specific Details */}
      {userData.role === 'vendor' && (
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="space-y-3">
            {/* Service Categories */}
            {(userData.serviceCategories || userData.vendorProfile?.serviceCategories) && (
              <div>
                <p className="text-xs text-gray-500 mb-2">Service Categories</p>
                <div className="flex flex-wrap gap-1">
                  {(userData.serviceCategories || userData.vendorProfile?.serviceCategories || []).slice(0, 3).map((category, idx) => (
                    <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </span>
                  ))}
                  {(userData.serviceCategories || userData.vendorProfile?.serviceCategories || []).length > 3 && (
                    <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded-full text-xs">
                      +{(userData.serviceCategories || userData.vendorProfile?.serviceCategories || []).length - 3}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              {userData.skills && userData.skills.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Skills</p>
                  <div className="flex flex-wrap gap-1">
                    {userData.skills.slice(0, 2).map((skill, idx) => (
                      <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                        {getSkillLabel(skill)}
                      </span>
                    ))}
                    {userData.skills.length > 2 && (
                      <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded-full text-xs">
                        +{userData.skills.length - 2}
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div>
                <p className="text-xs text-gray-500 mb-1">Experience</p>
                <p className="text-sm font-medium">{userData.experience || userData.vendorProfile?.experience || 0} years</p>
              </div>
              {(userData.hourlyRate > 0 || userData.vendorProfile?.hourlyRate > 0) && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Rate</p>
                  <p className="text-sm font-medium">${userData.hourlyRate || userData.vendorProfile?.hourlyRate}/hr</p>
                </div>
              )}
              <div>
                <p className="text-xs text-gray-500 mb-1">Rating</p>
                <div className="flex items-center">
                  <Star className="w-4 h-4 text-yellow-500 mr-1" />
                  <span className="text-sm font-medium">
                    {userData.accurateRating ?
                      `${userData.accurateRating.averageRating.toFixed(1)}/5 (${userData.accurateRating.totalRatings} reviews)` :
                      `${userData.rating || userData.vendorProfile?.averageRating || 0}/5`
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* Service Area */}
            {(userData.serviceArea || userData.vendorProfile?.serviceArea) && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Service Area</p>
                <p className="text-sm font-medium">{userData.serviceArea || userData.vendorProfile?.serviceArea}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Agent/Referral-specific Details */}
      {userData.role === 'referral' && (
        <div className="bg-purple-50 rounded-lg p-3 mb-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">Agent Code</p>
              <p className="text-sm font-mono font-medium text-purple-600">{userData.agentCode || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Commission Rate</p>
              <p className="text-sm font-medium">{userData.commissionRate || 15}%</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Total Earnings</p>
              <p className="text-sm font-medium">${(userData.totalCommissionEarned || 0).toFixed(2)}</p>
            </div>
            <div className="col-span-2 flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 mb-1">Status</p>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onUpdateAgent?.({
                      userId: userData._id,
                      type: 'status',
                      value: !userData.isAgentActive
                    })}
                    className={`p-1 rounded ${userData.isAgentActive ? 'text-green-600 hover:bg-green-50' : 'text-red-600 hover:bg-red-50'}`}
                    title={userData.isAgentActive ? 'Deactivate agent' : 'Activate agent'}
                  >
                    {userData.isAgentActive ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                  </button>
                  <span className={`text-xs font-medium ${userData.isAgentActive ? 'text-green-600' : 'text-red-600'}`}>
                    {userData.isAgentActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div>
                <button
                  onClick={() => {
                    const newRate = prompt('Enter new commission rate (%):', userData.commissionRate || 15);
                    if (newRate && !isNaN(newRate)) {
                      onUpdateAgent?.({
                        userId: userData._id,
                        type: 'commission',
                        value: parseFloat(newRate)
                      });
                    }
                  }}
                  className="text-purple-600 hover:bg-purple-50 p-1 rounded"
                  title="Update commission rate"
                >
                  <Settings className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timestamps */}
      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
        <span>Joined: {new Date(userData.createdAt).toLocaleDateString()}</span>
        <span>
          Last login: {userData.lastLogin ? new Date(userData.lastLogin).toLocaleDateString() : 'Never'}
        </span>
      </div>

      {/* Pending Approval Section */}
      {userData.status === 'PENDING' && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex space-x-2">
            <button
              onClick={() => onStatusChange?.(userData._id, 'ACTIVE')}
              className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              <UserCheck className="w-4 h-4 mr-1" />
              Approve
            </button>
            <button
              onClick={() => onStatusChange?.(userData._id, 'SUSPENDED')}
              className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
            >
              <UserX className="w-4 h-4 mr-1" />
              Reject
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons Section */}
      <div className="border-t border-gray-100 pt-4 mt-4">
        <div className="flex flex-wrap gap-2 justify-between items-center">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Edit button clicked for user:', userData._id);
                onEdit?.(userData);
              }}
              className="flex items-center px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
            >
              <Edit2 className="w-4 h-4 mr-1" />
              Edit User
            </button>

            {(userData.role === 'customer' || userData.role === 'referral') && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenPoints?.(userData);
                  }}
                  className="flex items-center px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
                >
                  <Gift className="w-4 h-4 mr-1" />
                  Manage Points
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenVouchers?.(userData);
                  }}
                  className="flex items-center px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
                >
                  <Tag className="w-4 h-4 mr-1" />
                  Manage Vouchers
                </button>
              </>
            )}

            {userData.role === 'vendor' && (
              <button
                onClick={() => {
                  console.log('View Certificates clicked for:', userData);
                  console.log('Vendor Certificates:', userData.vendorCertificates);
                  onViewCertificates?.(userData);
                }}
                className="flex items-center px-3 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors text-sm font-medium"
              >
                <FileText className="w-4 h-4 mr-1" />
                View Certificates
              </button>
            )}
          </div>

          <div className="flex gap-2">
            {/* Status Change Button */}
            {userData.ceaNumberStatus !== 'PENDING' && (
              <button
                onClick={() => onStatusChange?.(userData._id, userData.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE')}
                className={`flex items-center px-3 py-2 rounded-lg transition-colors text-sm font-medium ${
                  userData.status === 'ACTIVE'
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {userData.status === 'ACTIVE' ? (
                  <>
                    <UserX className="w-4 h-4 mr-1" />
                    Suspend
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4 mr-1" />
                    Activate
                  </>
                )}
              </button>
            )}

            {/* Delete Button (Super Admin Only) */}
            {isSuper && (
              <button
                onClick={() => onDelete?.(userData._id)}
                className="flex items-center px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default UserCard;
